// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type FormEvent } from 'react';
import { Button, Card, Input, Label, Stack } from '@intakeform/ui';
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  type ApiKeyScope,
  type CreatedApiKey,
} from '../../api/api-keys.js';

/**
 * Access levels offered at creation (intakeform-preview-iiyj). Forms only is
 * the default: most consumers just render our forms, and submission metadata
 * (who submitted what, when, flagged or not) is more than they need.
 */
const ACCESS_LEVELS: { value: ApiKeyScope; label: string }[] = [
  { value: 'forms:read', label: 'Forms only' },
  { value: 'read', label: 'Forms and submission metadata' },
];

const scopeLabel = (scopes: string[]): string =>
  scopes.includes('read')
    ? 'forms + submissions'
    : scopes.includes('forms:read')
      ? 'forms only'
      : scopes.join(', ');

/**
 * Admin-only panel for issuing/revoking API keys for the external
 * /api/v1 read API (1zo). The plaintext token is shown exactly once on
 * creation; afterwards only its non-secret prefix is displayed.
 */
export function ApiKeysPanel() {
  const { data: keys, error: loadError } = useApiKeys();
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();

  const [name, setName] = useState('');
  const [access, setAccess] = useState<ApiKeyScope>('forms:read');
  const [created, setCreated] = useState<CreatedApiKey | null>(null);

  const onCreate = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    create.mutate(
      { name: trimmed, scopes: [access] },
      {
        onSuccess: (key) => {
          setCreated(key);
          setName('');
        },
      },
    );
  };

  const allKeys = keys ?? [];
  const activeKeys = allKeys.filter((k) => k.revokedAt === null);
  const revokedCount = allKeys.length - activeKeys.length;

  return (
    <Card heading="API keys" variant="default" data-api-keys-panel>
      <p className="text-sm text-muted-foreground" style={{ marginBottom: 12 }}>
        Issue keys for the external read API (<code>/api/v1</code>). Authenticate with{' '}
        <code>Authorization: Bearer ik_…</code>. The full token is shown once at creation — copy it
        then; only its prefix is kept afterwards.
      </p>

      {loadError !== null && loadError !== undefined ? (
        <div className="error-banner text-sm" role="alert" style={{ marginBottom: 12 }}>
          {loadError.message}
        </div>
      ) : null}

      {created !== null ? (
        <div
          role="status"
          aria-live="polite"
          className="text-sm"
          style={{
            marginBottom: 16,
            padding: 12,
            border: '1px solid rgb(var(--color-border))',
            borderRadius: 'var(--radius-md)',
            background: 'rgb(var(--color-surface))',
          }}
          data-api-key-created
        >
          <strong>Key “{created.name}” created.</strong> Copy it now — it won’t be shown again:
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 8,
            }}
          >
            <code style={{ wordBreak: 'break-all', flex: 1 }}>{created.token}</code>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(created.token);
              }}
            >
              Copy
            </Button>
            <Button type="button" variant="ghost" onClick={() => setCreated(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      ) : null}

      <form onSubmit={onCreate} style={{ maxWidth: 560 }}>
        <Stack gap={16}>
          <Stack gap={4}>
            <Label htmlFor="api-key-name" required>
              Key name
            </Label>
            <Input
              id="api-key-name"
              type="text"
              value={name}
              data-api-key-name
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="Booking service (prod)"
            />
          </Stack>

          <Stack gap={4}>
            <Label htmlFor="api-key-access">Access</Label>
            <select
              id="api-key-access"
              data-api-key-access
              value={access}
              onChange={(e) => setAccess(e.currentTarget.value as ApiKeyScope)}
              className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {ACCESS_LEVELS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </Stack>

          {create.error !== null && create.error !== undefined ? (
            <div className="error-banner text-sm" role="alert" aria-live="polite">
              {create.error.message}
            </div>
          ) : null}

          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={create.isPending || name.trim().length === 0}
            >
              {create.isPending ? 'Creating…' : 'Create key'}
            </Button>
          </div>
        </Stack>
      </form>

      <div style={{ marginTop: 20 }}>
        {activeKeys.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active keys.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-api-keys-list>
            {activeKeys.map((k) => (
              <li
                key={k.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 0',
                  borderBottom: '1px solid rgb(var(--color-border))',
                }}
              >
                <span className="text-sm">
                  <code>{k.prefix}…</code> · {k.name} · {scopeLabel(k.scopes)} ·{' '}
                  <span className="text-muted-foreground">
                    {k.lastUsedAt !== null
                      ? `last used ${new Date(k.lastUsedAt).toLocaleDateString()}`
                      : 'never used'}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="danger"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(k.id)}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
        {revokedCount > 0 ? (
          <p className="text-xs text-muted-foreground" style={{ marginTop: 8 }}>
            {revokedCount} revoked key{revokedCount === 1 ? '' : 's'} hidden.
          </p>
        ) : null}
      </div>
    </Card>
  );
}
